import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { useSettings } from "../lib/settings";
import { api } from "../lib/api";

export default function Hero() {
  const { settings } = useSettings();
  const [tiles, setTiles] = useState([]);
  const [bestSeller, setBestSeller] = useState(null);

  useEffect(() => {
    api.get("/hero-tiles").then((r) => setTiles(r.data.slice(0, 4))).catch(() => setTiles([]));
    api.get("/products/best-seller").then((r) => setBestSeller(r.data?.product || null)).catch(() => {});
  }, []);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F5F2EA] text-xs uppercase tracking-[0.3em] thick-gold !border-[3px]"
              style={{ borderColor: "var(--accent-strong)" }}>
              <Sparkles className="w-3 h-3 text-[var(--accent-strong)]" /> {settings.hero_eyebrow || "Creator Marketplace"}
            </span>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-[#2C2924]">
              {settings.hero_heading_part1 || "Prompts, ebooks &"}<br />
              <span className="emerald-heading italic">{settings.hero_heading_accent || "aesthetic things"}</span><br />
              {settings.hero_heading_part2 || "I actually use."}
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-[#5C5547] leading-relaxed">
              {(settings.hero_subtitle || "").replace("{brand}", settings.brand_name || "This That For Me")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={settings.hero_cta_primary_link || "/shop"} data-testid="hero-shop-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] transition text-sm font-medium">
                {settings.hero_cta_primary_label || "Browse the shop"} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to={settings.hero_cta_secondary_link || "/shop?category=ai-prompts"} data-testid="hero-prompts-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-black hover:opacity-90 transition text-sm font-semibold"
                style={{ background: "var(--accent)" }}>
                {settings.hero_cta_secondary_label || "Shop AI Prompts"}
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8 text-sm text-[#5C5547]">
              <div><span className="font-serif text-2xl text-[#2C2924]">{settings.stat_drops || "15+"}</span><div className="uppercase text-[10px] tracking-[0.2em]">Curated drops</div></div>
              <div className="w-px h-10 bg-[#EAE4D3]" />
              <div><span className="font-serif text-2xl text-[#2C2924]">{settings.stat_rating || "5★"}</span><div className="uppercase text-[10px] tracking-[0.2em]">Avg rating</div></div>
              <div className="w-px h-10 bg-[#EAE4D3]" />
              <div><span className="font-serif text-2xl text-[#2C2924]">{settings.stat_buyers || "2k+"}</span><div className="uppercase text-[10px] tracking-[0.2em]">Happy buyers</div></div>
            </div>
          </div>

          {/* 4 equal clickable tiles, 2x2 grid */}
          <div className="md:col-span-5 fade-up delay-2">
            <div className="grid grid-cols-2 gap-4">
              {tiles.map((t, i) => (
                <HeroTile key={t.id || i} tile={t} index={i} bestSeller={i === 0 ? bestSeller : null} />
              ))}
              {tiles.length < 4 && Array.from({ length: 4 - tiles.length }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square rounded-3xl bg-[#F5F2EA] thick-gold" />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[var(--accent)]/15 blur-3xl pointer-events-none" />
    </section>
  );
}

function HeroTile({ tile, index, bestSeller }) {
  const showBadge = index === 0 && bestSeller;
  const inner = (
    <div className="group relative aspect-square rounded-3xl overflow-hidden bg-[#F5F2EA] thick-gold transition hover:-translate-y-1"
      style={{ animationDelay: `${0.1 * index}s` }}>
      <img src={tile.image_url} alt={tile.label || ""} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      {showBadge && (
        <div data-testid="best-seller-badge"
          className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold text-white"
          style={{ background: "linear-gradient(95deg, #1F6B52, #0F3D2E)", boxShadow: "0 0 0 2px var(--accent-strong), 0 4px 12px rgba(0,0,0,0.3)" }}>
          <Sparkles className="w-3 h-3" /> Best Seller
        </div>
      )}
      {tile.label && (
        <div className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="text-white text-xs font-semibold tracking-wide line-clamp-2">{tile.label}</div>
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-[var(--accent-soft)]">
            View <ArrowUpRight className="w-3 h-3" />
          </div>
        </div>
      )}
    </div>
  );
  if (!tile.link) return inner;
  if (tile.link.startsWith("/")) return <Link to={tile.link} data-testid={`hero-tile-${index}`}>{inner}</Link>;
  return <a href={tile.link} target="_blank" rel="noreferrer" data-testid={`hero-tile-${index}`}>{inner}</a>;
}
